import React from 'react';
import { Box, InputAdornment, ToggleButton, ToggleButtonGroup } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { RHFTextField } from '../../../../components/RHF';
import { LoginFormProps, LoginMethod } from '../../types';
import callIconImage from '../../../../assets/images/callIcon.svg';
import { formatPhoneNumber, getDigitsOnly } from 'utils';
import { CustomButton } from 'components/Button';
import { CustomCheckbox } from 'components/CustomCheckbox';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  loading,
  identifier,
  loginMethod,
  onLoginMethodChange,
  methods,
  isChecked,
  showConsent,
  consentLoading,
  handleCheckboxChange
}) => {
  const handleCustomCheckboxChange = (event: React.ChangeEvent<HTMLInputElement> | { target: { checked: boolean } }) => {
    if ('target' in event) {
      handleCheckboxChange(event as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const isEmail = loginMethod === 'email';
  const digitsOnly = getDigitsOnly(identifier || '');
  const isIdentifierValid = isEmail
    ? EMAIL_REGEX.test((identifier || '').trim())
    : digitsOnly.length === 10;
  const consentRequired = !isEmail && showConsent && !isChecked;
  const consentCopy = loginMethod === 'voice'
    ? 'By clicking "Request OTP", you consent to receive a One-Time Password via phone call on the provided phone number for secure sign-in.'
    : 'By clicking "Request OTP", you consent to receive a One-Time Password via SMS on the provided phone number for secure sign-in.';

  const handleMethodChange = (_event: React.MouseEvent<HTMLElement>, value: LoginMethod | null) => {
    if (value) {
      onLoginMethodChange(value);
    }
  };

  return (
    <Box className={"textfieldCont"}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={loginMethod}
        onChange={handleMethodChange}
        aria-label="Login method"
        sx={{
          mb: 2,
          gap: 1,
          '& .MuiToggleButtonGroup-grouped': {
            border: '0 !important',
            borderLeft: '0 !important',
            borderRadius: '8px !important',
            color: '#344054',
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '20px',
            textTransform: 'none',
            py: 1,
            px: 1,
          },
          '& .Mui-selected': {
            backgroundColor: '#EAF7F0 !important',
            color: '#1B6B45 !important',
          },
        }}
      >
        <ToggleButton value="email" aria-label="Email">
          {loginMethod === 'email' ? <RadioButtonCheckedIcon fontSize="small" sx={{ mr: 1, color: '#98A2B3' }} /> : <RadioButtonUncheckedIcon fontSize="small" sx={{ mr: 1, color: '#98A2B3' }} />}
          <EmailOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
          Email
        </ToggleButton>
        <ToggleButton value="sms" aria-label="Text">
          {loginMethod === 'sms' ? <RadioButtonCheckedIcon fontSize="small" sx={{ mr: 1, color: '#98A2B3' }} /> : <RadioButtonUncheckedIcon fontSize="small" sx={{ mr: 1, color: '#98A2B3' }} />}
          <SmsOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
          Text
        </ToggleButton>
        <ToggleButton value="voice" aria-label="Call">
          {loginMethod === 'voice' ? <RadioButtonCheckedIcon fontSize="small" sx={{ mr: 1, color: '#98A2B3' }} /> : <RadioButtonUncheckedIcon fontSize="small" sx={{ mr: 1, color: '#98A2B3' }} />}
          <PhoneInTalkOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
          Call
        </ToggleButton>
      </ToggleButtonGroup>

      <RHFTextField
        name="identifier"
        placeholder={isEmail ? "Email Address" : "Phone Number"}
        type={isEmail ? "email" : "tel"}
        inputProps={{ maxLength: isEmail ? 254 : 14 }}
        value={isEmail ? (identifier || '') : (identifier ? formatPhoneNumber(identifier) : '')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;

          if (isEmail) {
            methods.setValue('identifier', value);
            return;
          }

          const digitsOnly = getDigitsOnly(value);

          if (digitsOnly.length <= 10) {
            methods.setValue('identifier', digitsOnly);
          }
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                {isEmail ? <EmailOutlinedIcon fontSize="small" /> : <img src={callIconImage} alt="call" />}
              </InputAdornment>
            ),
          },
        }}
      />

      {showConsent && (
        <div className='d-flex mt-16'>
          <CustomCheckbox checked={!!isChecked} onChange={handleCustomCheckboxChange} className="mr-8" />
          <p className="agree-text">
            {consentCopy}
          </p>
        </div>
      )}

      <CustomButton
        loading={loading}
        onClick={onSubmit}
        className="button-base button-compact auth-form-button"
        disabled={!isIdentifierValid || consentRequired || consentLoading}
      >
        Request OTP
      </CustomButton>
    </Box>
  );
};

export default LoginForm;
